import { useInfiniteQuery } from '@tanstack/react-query';
import { postsRepository } from '../repositories/supabasePostsRepository';

export interface Post {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  visibility: string;
  media_url?: string | null;
  media_type?: 'image' | 'gif' | 'video' | null;
  author: {
    full_name: string | null;
    belt_rank: string | null;
    profile_picture_url: string | null;
    username: string | null;
    main_academy_id: string | null;
    academy_name?: string | null;
    is_instructor?: boolean;
    is_premium?: boolean;
    profession?: string | null;
    interests?: string[];
  };
}

const POSTS_PER_PAGE = 10;

export interface UsePostsOptions {
  visibility?: string;
  visibilities?: string[];
  extraKey?: any;
  interests?: string[];
  instructorsOnly?: boolean;
}

export const usePosts = (options: UsePostsOptions = {}) => {
  const { visibility, visibilities, extraKey } = options;
  const queryKey = visibilities ? ['posts', ...visibilities, extraKey] : ['posts', visibility || 'public', extraKey];
  return useInfiniteQuery({
    queryKey,
    initialPageParam: 0,
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const data = await postsRepository.getPosts(pageParam, POSTS_PER_PAGE);
      return data
        .filter(post => {
          const profile = Array.isArray(post.profiles) ? post.profiles[0] : post.profiles;
          // Instructor filter on the frontend
          if (options.instructorsOnly) {
            const isInstructor = profile?.is_instructor === true;
            const profession = (profile?.profession || '').toLowerCase();
            if (!(isInstructor || profession.includes('instructor'))) {
              return false;
            }
          }
          if (options.interests && options.interests.length > 0) {
            const hobbies = Array.isArray(profile?.profile_hobbies)
              ? profile.profile_hobbies.map((h: any) => (h && h.hobby && typeof h.hobby.name === 'string' ? h.hobby.name : null)).filter((n: string | null): n is string => Boolean(n))
              : [];
            // Only accept if there is an intersection between hobbies and selected interests
            return hobbies.length > 0 && hobbies.some((h: string) => options.interests!.includes(h));
          }
          return true;
        })
        .map((post) => {
          const profile = Array.isArray(post.profiles) ? post.profiles[0] : post.profiles;
          return {
            id: post.id,
            content: post.content,
            created_at: post.created_at,
            user_id: post.user_id,
            visibility: post.visibility,
            media_url: post.media_url ?? null,
            media_type: (post.media_type as 'image' | 'gif' | 'video' | null) ?? null,
            author: {
              full_name: profile?.full_name || null,
              belt_rank: profile?.belt_rank || 'white',
              profile_picture_url: profile?.profile_picture_url || null,
              username: profile?.username || null,
              main_academy_id: profile?.main_academy_id || null,
              academy_name: (profile?.academies && !Array.isArray(profile.academies)) ? profile.academies.name || null : (Array.isArray(profile?.academies) ? profile.academies[0]?.name || null : null),
              is_instructor: profile?.is_instructor || false,
              is_premium: profile?.is_premium || false,
              profession: profile?.profession || null,
              interests: Array.isArray(profile?.profile_hobbies) 
                ? profile.profile_hobbies.map((h: any) => h?.hobby?.name).filter((name: string | null): name is string => Boolean(name)) 
                : [],
            },
          };
        });
    },
    getNextPageParam: (lastPage: Post[], allPages: Post[][]) => {
      return lastPage.length === POSTS_PER_PAGE ? allPages.length : undefined;
    },
  });
}; 